<template>
  <span ref="rootRef" class="materin-ui-autocomplete" :class="{ 'is-open': open && filtered.length > 0 }">
    <input
      class="materin-ui-autocomplete__field materin-ui-field__inner"
      type="text"
      :value="modelValue"
      :placeholder="placeholder"
      @input="onInput"
      @keydown="onKeydown"
    />

    <ul v-if="open && filtered.length" class="materin-ui-autocomplete__list" role="listbox">
      <li
        v-for="(option, i) in filtered"
        :key="i"
        class="materin-ui-autocomplete__option"
        :class="{ 'is-active': i === cursor }"
        role="option"
        :aria-selected="i === cursor"
        @mousedown.prevent="choose(option)"
      >
        {{ option }}
      </li>
    </ul>
  </span>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref } from 'vue'

defineOptions({ name: 'MiAutocomplete' })

interface AutocompleteProps {
  modelValue?: string
  suggestions?: string[]
  placeholder?: string
}

const props = withDefaults(defineProps<AutocompleteProps>(), {
  modelValue: '',
  suggestions: () => [],
  placeholder: ''
})

const emit = defineEmits<{ (e: 'update:modelValue', value: string): void }>()

const rootRef = ref<HTMLElement>()
const open = ref(false)
const cursor = ref(0)

const filtered = computed(() => {
  const query = props.modelValue.trim().toLowerCase()
  if (!query) return []
  return props.suggestions.filter((item) => item.toLowerCase().includes(query)).slice(0, 8)
})

const onInput = (event: Event) => {
  const value = (event.target as HTMLInputElement).value
  emit('update:modelValue', value)
  cursor.value = 0
  open.value = true
}

const onKeydown = (event: KeyboardEvent) => {
  if (event.key === 'ArrowDown') {
    cursor.value = Math.min(filtered.value.length - 1, cursor.value + 1)
  } else if (event.key === 'ArrowUp') {
    cursor.value = Math.max(0, cursor.value - 1)
  } else if (event.key === 'Enter' && open.value) {
    choose(filtered.value[cursor.value] ?? props.modelValue)
  } else if (event.key === 'Escape') {
    open.value = false
  }
}

const choose = (value: string) => {
  emit('update:modelValue', value)
  open.value = false
}

const onDocClick = (event: MouseEvent) => {
  if (rootRef.value && !rootRef.value.contains(event.target as Node)) open.value = false
}

if (typeof document !== 'undefined') document.addEventListener('click', onDocClick, true)
onUnmounted(() => {
  if (typeof document !== 'undefined') document.removeEventListener('click', onDocClick, true)
})
</script>

<style lang="scss">
@use '@/styles/tokens.scss' as *;

.materin-ui-autocomplete {
  position: relative;
  display: inline-block;

  &__field {
    min-width: 180px;
  }

  &__list {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    z-index: 2100;
    margin: 0;
    padding: $materin-space-1;
    border: 1px solid $materin-glass-border;
    border-radius: $materin-radius;
    background: $materin-glass-bg-strong;
    backdrop-filter: $materin-glass-blur;
    -webkit-backdrop-filter: $materin-glass-blur;
    box-shadow: $materin-elev-1;
    list-style: none;
  }

  &__option {
    padding: $materin-space-2 $materin-space-3;
    border-radius: $materin-radius;
    color: $materin-text;
    font-size: $materin-font-size-small;
    cursor: pointer;

    &.is-active {
      background: $materin-surface-4;
    }
  }
}
</style>
